import React, { useEffect, useState, useMemo } from "react";
import StepCard from "../../stories/Cards/StepCard";
import step1 from "../../assets/images/confirmation-steps/step-1.svg";
import step2 from "../../assets/images/confirmation-steps/step-2.svg";
import step3 from "../../assets/images/confirmation-steps/step-3.svg";
import step4 from "../../assets/images/confirmation-steps/step-4.svg";
import step5 from "../../assets/images/confirmation-steps/step-5.svg";
import step6 from "../../assets/images/confirmation-steps/step-6.svg";
import step7 from "../../assets/images/confirmation-steps/step-7.svg";
import { Button } from "../../stories/Buttons/Button";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { useParams, useNavigate, useLocation } from "react-router";
import useApis from "../../services/useAPI";
import SuccessModal from "../../stories/Modals/Modal";
import logo from "../../assets/images/boots-myhealthchecked-logo.svg";
import img from "../../assets/images/notification.svg";
import { links } from "../../stories/Forms/fields";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import CheckList from "./CheckList";
import infoIcon from "../../assets/icons/Information-fill.svg";
import { clinicalIcons } from "../../assets/images/checklist";
import { clinicalSteps } from "../../utils/constants";
import { XCircleFill} from "react-bootstrap-icons";

const clinicalBodies = {
  2: (
    <span>
      <strong>Prepare for centrifugation.</strong>
      <br />
      For optimal sample integrity,{" "}
      <strong>begin centrifugation within one hour of</strong> venipuncture.
      Ensure the centrifuge is set using <strong>RCF (1000–1300 g)</strong>.
      <span className="vh-note">
        <img alt="" src={infoIcon} />
        NOTE: not rpm.
      </span>
    </span>
  ),
  7: "Store the samples in a refrigerator at a temperature 2-8 °C until it is collected by the courier.",
};

const ClinicalConfirmationSteps = () => {
  const [selectedSteps, setSelectedSteps] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  // Extract id from URL parameters
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Define steps with conditional images based on the presence of secondary_barcode
  const steps = useMemo(
    () => [
      { number: 1, image: step1, body: clinicalSteps.Box1 },
      { number: 2, image: step2, body: clinicalSteps.Box2 },
      { number: 3, image: step3, body: clinicalSteps.Box3 },
      { number: 4, image: step4, body: clinicalSteps.Box4 },
      { number: 5, image: step5, body: clinicalSteps.Box5 },
      { number: 6, image: step6, body: clinicalSteps.Box6 },
      { number: 7, image: step7, body: clinicalSteps.Box7 },
    ],
    []
  );

  const postUrl = `/api/phlebotomists-portal/register-appointment/${id}/`;
  const {
    callApi,
    modalShow,
    tokenError,
    toggleShowError,
    setModalShow,
    serverError
  } = useApis();

useEffect(() => {
  if (serverError || tokenError) {
    auth?.showToast(
      "Error",
      tokenError || serverError,
      "../assets/mhc.png",
      {icon: <XCircleFill />}
    );
  }
}, [serverError, tokenError]);

  const auth = useAuth();

    const isVhClinical =
      auth?.featureFlag && localStorage.getItem("selectedService") === "clinical";
    const stages = isVhClinical
      ? ["enter-barcode", "appointment-confirmation"]
      : ["appointment-confirmation"];
    
    useEffect(() => {
      if (!stages.includes(sessionStorage.getItem("allowedStage")) && auth?.featureFlag) {
        navigate("/patient-record");
      }
    },[sessionStorage.getItem("allowedStage"), auth?.featureFlag, navigate]);

  // Set the title when the component mounts or when certain dependencies change
  useEffect(() => {
    auth.setTitle("Clinical studies");
  }, [auth]);

  // Handle step card click to toggle selection
  const handleCardClick = (number) => {
    setSelectedSteps((prevSelectedSteps) =>
      prevSelectedSteps.includes(number)
        ? prevSelectedSteps.filter((step) => step !== number)
        : [...prevSelectedSteps, number]
    );
    setErrorMessage("");
  };

// Handle confirm button click to validate step completion and make API call
const handleConfirm = () => {
  const allStepsSelected = selectedSteps.length === steps.length;

  if (allStepsSelected) {
    if (isVhClinical) {
      auth?.allowStage("appointment-confirmation");
      navigate(`/appointment-confirmation/${id}`, {
        state: { study: location.state?.study },
        replace: true,
      });
      return;
    }
    callApi(null, postUrl, "POST", {
      Authorization: `Bearer ${
        localStorage.getItem("access_token") || auth.accessToken
      }`,
      "Content-Type": "application/json",
    });
    localStorage.removeItem("secondary_barcode");
    return;
  }
  setErrorMessage(
    "Please ensure all steps are completed before proceeding"
  );
};


  const handleRedirect = () => {
    navigate("/patient-record");
  };

  const successModal = modalShow && (
    <SuccessModal
      handleClose={""}
      href={"/patient-record"}
      heading="Success"
      label={"Return to home"}
      message={"The test has been successfully registered."}
      onHide={() => {
        setModalShow(false);
        setTimeout(() => {
          navigate("/patient-record");
        }, 500);
      }}
      redirect={handleRedirect}
      show={modalShow}
      variant={"primary"}
      close={false}
      logo={true}
    />
  );

  if (auth?.featureFlag) {
    return (
      <CheckList
        bodies={clinicalBodies}
        icons={clinicalIcons}
        cardSubtitle="Before returning the sample, please click to confirm all the below steps have been followed:"
        cardTitle="For two tube test:"
        className="vh-confirm-card--clinical"
        heading="Prepare the sample for storage and collection"
        subHeading={
          <>
            Complete and acknowledge all checklist items.
            <br />
            Once all items have been checked and acknowledged, click ‘<strong>Continue</strong>’.
          </>
        }
        onContinue={handleConfirm}
        onToggle={handleCardClick}
        selectedSteps={selectedSteps}
        steps={steps}
      >
        {successModal}
      </CheckList>
    );
  }

  return (
    <LoggedInLayout
      buttonLabel={"Logout"}
      img={img}
      isActivation={false}
      links={links}
      logo={logo}
      pageTitle={auth.title}
      userInitials={auth.userInitials}
    >
      <div className="patient-record-form">
        <h3>Thrive Registry</h3>
        <p>
          Test : <span>Thrive Panel</span>
        </p>
        <div className="patient-record-card p-0 clinical-step-form">
          <div className="px-4 pt-4">
            <h2 className="mb-1">Appointment confirmation</h2>{" "}
            <p>Please confirm details to continue registration</p>
          </div>
          <div className="patient-card-seperator"></div>
          <div className="container py-0 px-4">
            <div className="row">
              <div className="col-md-12">
                <h3>
                  Before returning the sample,{" "}
                  <u>please click to confirm the following steps:</u>
                </h3>
              </div>
            </div>
          </div>
          <div className="container pt-0 pb-4 px-4">
            <div className="row cards">
              {steps.map((step) => (
                <StepCard
                  key={step.number}
                  {...step}
                  isSelected={selectedSteps.includes(step.number)}
                  onClick={() => handleCardClick(step.number)}
                />
              ))}

              {errorMessage && (
                <div className="server-error">{errorMessage}</div>
              )}
            </div>
          </div>
          {selectedSteps.length === steps.length && (
            <div className="outside-actions">
            <Button
              className="mt-4 mr-4 float-end btn btn-primary btn-rounded"
              label={"Confirm"}
              onClick={handleConfirm}
              size={"small"}
              variant={"secondary"}
            />
            </div>
          )}
        </div>
      </div>

      {successModal}
    </LoggedInLayout>
  );
};

export default ClinicalConfirmationSteps;